<template>
  <div class="db-analyzer">
    <APageHead title="데이터베이스 성능 분석" sub="쿼리 성능과 인덱스 사용률을 모니터링하세요.">
      <template #actions>
        <ABtn kind="ghost" icon="refresh" @click="refreshAll">새로고침</ABtn>
      </template>
    </APageHead>

    <div v-if="stats" class="db-analyzer__stats">
      <ACard>
        <div class="db-analyzer__stat-row">
          <AStat label="슬로우 쿼리" :value="String(stats.slow_query_count || 0)" />
          <div class="db-analyzer__stat-icon db-analyzer__stat-icon--warn">
            <i class="material-icons-round">hourglass_top</i>
          </div>
        </div>
        <p class="db-analyzer__stat-foot">오늘: {{ stats.slow_query_today || 0 }}건</p>
      </ACard>
      <ACard>
        <div class="db-analyzer__stat-row">
          <AStat label="평균 쿼리 시간" :value="formatMs(stats.avg_query_time_ms)" unit="ms" />
          <div class="db-analyzer__stat-icon db-analyzer__stat-icon--info">
            <i class="material-icons-round">speed</i>
          </div>
        </div>
      </ACard>
      <ACard>
        <div class="db-analyzer__stat-row">
          <AStat label="인덱스 사용률" :value="String(stats.index_usage_rate || 0)" unit="%" />
          <div class="db-analyzer__stat-icon db-analyzer__stat-icon--ok">
            <i class="material-icons-round">insights</i>
          </div>
        </div>
        <p class="db-analyzer__stat-foot">Index Hit Rate</p>
      </ACard>
      <ACard>
        <div class="db-analyzer__stat-row">
          <AStat label="총 쿼리 수" :value="formatNumber(stats.total_queries_24h)" />
          <div class="db-analyzer__stat-icon">
            <i class="material-icons-round">query_stats</i>
          </div>
        </div>
        <p class="db-analyzer__stat-foot">최근 24시간</p>
      </ACard>
    </div>

    <ACard body-flush>
      <div class="p-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="p-tabs__item"
          :class="{ 'is-active': activeTab === tab.id }"
          @click="activeTab = tab.id"
        >
          <i class="material-icons-round">{{ tab.icon }}</i>
          {{ tab.label }}
        </button>
      </div>

      <div class="db-analyzer__pane">
        <!-- 슬로우 쿼리 -->
        <div v-if="activeTab === 'slow'">
          <div class="p-form-grid p-form-grid--3 db-analyzer__filters">
            <div class="p-field">
              <label class="p-field__label">실행 시간 임계값</label>
              <select v-model="slowFilters.threshold" class="p-input">
                <option value="1">1초 이상</option>
                <option value="2">2초 이상</option>
                <option value="5">5초 이상</option>
                <option value="10">10초 이상</option>
              </select>
            </div>
            <div class="p-field">
              <label class="p-field__label">페이지 크기</label>
              <select v-model="slowFilters.page_size" class="p-input">
                <option value="20">20개</option>
                <option value="50">50개</option>
                <option value="100">100개</option>
              </select>
            </div>
            <div class="db-analyzer__filter-action">
              <ABtn kind="yellow" icon="search" @click="fetchSlowQueries">검색</ABtn>
            </div>
          </div>

          <AEmpty
            v-if="!loadingSlowQueries && slowQueries.length === 0"
            title="슬로우 쿼리가 없습니다"
            sub="데이터베이스가 최적의 상태로 운영되고 있습니다"
          />

          <ATable
            v-else
            :columns="slowCols"
            :rows="slowQueries"
          >
            <template #cell:mean_time_sec="{ row }">
              <APill kind="warn">{{ formatSec(row.mean_time_sec) }}s</APill>
            </template>
            <template #cell:max_time_sec="{ row }">
              <span class="db-analyzer__num">{{ formatSec(row.max_time_sec) }}s</span>
            </template>
            <template #cell:calls="{ row }">
              <span class="db-analyzer__num">{{ formatNumber(row.calls) }}</span>
            </template>
            <template #cell:query="{ row }">
              <div class="db-analyzer__query" :title="row.query">{{ row.query }}</div>
            </template>
            <template #cell:actions="{ row }">
              <ABtn kind="ghost" size="sm" icon="visibility" icon-only @click="showQueryDetail(row)" />
            </template>
          </ATable>

          <div v-if="slowTotalPages > 1" class="db-analyzer__pager">
            <ABtn
              kind="ghost"
              size="sm"
              icon="chevron_left"
              icon-only
              :disabled="slowCurrentPage === 1"
              @click="changeSlowPage(slowCurrentPage - 1)"
            />
            <button
              v-for="page in slowDisplayPages"
              :key="page"
              type="button"
              class="db-analyzer__pager-num"
              :class="{ 'is-active': page === slowCurrentPage }"
              @click="changeSlowPage(page)"
            >{{ page }}</button>
            <ABtn
              kind="ghost"
              size="sm"
              icon="chevron_right"
              icon-only
              :disabled="slowCurrentPage === slowTotalPages"
              @click="changeSlowPage(slowCurrentPage + 1)"
            />
          </div>
        </div>

        <!-- 자주 실행되는 쿼리 -->
        <div v-else-if="activeTab === 'frequent'">
          <ACard class="db-analyzer__chart-card">
            <h5 class="db-analyzer__sub-h">
              <i class="material-icons-round">bar_chart</i>
              TOP 10 쿼리 실행 횟수
            </h5>
            <div class="db-analyzer__chart-wrap">
              <canvas ref="frequentChart"></canvas>
            </div>
          </ACard>

          <AEmpty
            v-if="!loadingFrequentQueries && frequentQueries.length === 0"
            title="데이터가 없습니다"
          />

          <ATable
            v-else
            :columns="frequentCols"
            :rows="frequentQueries"
            row-key="queryid"
          >
            <template #cell:rank="{ row }">
              <APill kind="yellow">{{ row.rank }}</APill>
            </template>
            <template #cell:query="{ row }">
              <div class="db-analyzer__query" :title="row.query">{{ row.query }}</div>
            </template>
            <template #cell:calls="{ row }">
              <strong class="db-analyzer__num">{{ formatNumber(row.calls) }}</strong>
            </template>
            <template #cell:mean_time_ms="{ row }">
              <span class="db-analyzer__num">{{ formatMs(row.mean_time_ms) }} ms</span>
            </template>
            <template #cell:total_time_ms="{ row }">
              <span class="db-analyzer__num">{{ formatTotalTime(row.total_time_ms) }}</span>
            </template>
            <template #cell:hit_rate="{ row }">
              <APill :kind="hitRateKind(row.hit_rate)">{{ formatPercent(row.hit_rate) }}%</APill>
            </template>
            <template #cell:actions="{ row }">
              <ABtn kind="ghost" size="sm" icon="visibility" icon-only @click="showQueryDetail(row)" />
            </template>
          </ATable>
        </div>

        <!-- 인덱스 -->
        <div v-else-if="activeTab === 'indexes'">
          <div
            v-if="indexSummary && indexSummary.unused_indexes > 0"
            class="db-analyzer__alert"
          >
            <i class="material-icons-round">warning</i>
            <strong>미사용 인덱스 {{ indexSummary.unused_indexes }}개</strong>
            <span>가 발견되었습니다. 삭제를 고려해보세요.</span>
          </div>

          <div v-if="indexSummary" class="db-analyzer__idx-summary">
            <ACard>
              <div class="db-analyzer__idx-stat">
                <span class="db-analyzer__idx-val">{{ indexSummary.total_indexes || 0 }}</span>
                <span class="db-analyzer__idx-label">전체 인덱스</span>
              </div>
            </ACard>
            <ACard>
              <div class="db-analyzer__idx-stat">
                <span class="db-analyzer__idx-val db-analyzer__idx-val--ok">{{ indexSummary.used_indexes || 0 }}</span>
                <span class="db-analyzer__idx-label">사용 중</span>
              </div>
            </ACard>
            <ACard>
              <div class="db-analyzer__idx-stat">
                <span class="db-analyzer__idx-val db-analyzer__idx-val--err">{{ indexSummary.unused_indexes || 0 }}</span>
                <span class="db-analyzer__idx-label">미사용</span>
              </div>
            </ACard>
            <ACard>
              <div class="db-analyzer__idx-stat">
                <span class="db-analyzer__idx-val db-analyzer__idx-val--info">{{ indexSummary.total_index_size || '0 MB' }}</span>
                <span class="db-analyzer__idx-label">총 인덱스 크기</span>
              </div>
            </ACard>
          </div>

          <div class="p-form-grid p-form-grid--3 db-analyzer__filters">
            <div class="p-field">
              <label class="p-field__label">테이블명</label>
              <input v-model="indexFilters.table_name" class="p-input" placeholder="테이블명 검색" />
            </div>
            <div class="db-analyzer__filter-action">
              <ABtn kind="yellow" icon="search" @click="fetchIndexes">검색</ABtn>
            </div>
          </div>

          <AEmpty
            v-if="!loadingIndexes && indexes.length === 0"
            title="인덱스 정보가 없습니다"
          />

          <ATable
            v-else
            :columns="indexCols"
            :rows="indexes"
          >
            <template #cell:table_name="{ row }">
              <strong>{{ row.table_name }}</strong>
            </template>
            <template #cell:index_name="{ row }">
              <code class="db-analyzer__code">{{ row.index_name }}</code>
            </template>
            <template #cell:index_size="{ row }">
              <span class="db-analyzer__num">{{ row.index_size }}</span>
            </template>
            <template #cell:idx_scan="{ row }">
              <span class="db-analyzer__num">{{ formatNumber(row.idx_scan) }}</span>
            </template>
            <template #cell:status="{ row }">
              <APill v-if="row.idx_scan === 0" kind="err">미사용</APill>
              <APill v-else kind="ok">사용 중</APill>
            </template>
          </ATable>
        </div>
      </div>
    </ACard>

    <!-- Query Detail Modal -->
    <AModal v-model="queryDetailOpen" title="쿼리 상세 정보" size="lg">
      <div v-if="selectedQuery" class="db-analyzer__detail">
        <div class="db-analyzer__detail-section">
          <h6 class="db-analyzer__detail-h">쿼리</h6>
          <pre class="db-analyzer__detail-code">{{ selectedQuery.query }}</pre>
        </div>

        <div class="db-analyzer__detail-grid">
          <div>
            <h6 class="db-analyzer__detail-h">실행 통계</h6>
            <table class="db-analyzer__detail-table">
              <tr><th>호출 횟수</th><td>{{ formatNumber(selectedQuery.calls) }}</td></tr>
              <tr><th>평균 실행 시간</th><td>{{ formatMs(selectedQuery.mean_time_ms || selectedQuery.mean_time_sec * 1000) }} ms</td></tr>
              <tr><th>최대 실행 시간</th><td>{{ formatMs(selectedQuery.max_time_ms || (selectedQuery.max_time_sec || 0) * 1000) }} ms</td></tr>
              <tr><th>총 실행 시간</th><td>{{ formatTotalTime(selectedQuery.total_time_ms || selectedQuery.total_time_sec * 1000) }}</td></tr>
            </table>
          </div>
          <div>
            <h6 class="db-analyzer__detail-h">리소스 정보</h6>
            <table class="db-analyzer__detail-table">
              <tr><th>반환 행 수</th><td>{{ formatNumber(selectedQuery.rows) }}</td></tr>
              <tr v-if="selectedQuery.rows_per_call !== undefined">
                <th>호출당 행 수</th><td>{{ formatMs(selectedQuery.rows_per_call) }}</td>
              </tr>
              <tr v-if="selectedQuery.hit_rate !== undefined">
                <th>Buffer Hit Rate</th>
                <td><APill :kind="hitRateKind(selectedQuery.hit_rate)">{{ formatPercent(selectedQuery.hit_rate) }}%</APill></td>
              </tr>
              <tr v-if="selectedQuery.user">
                <th>사용자</th><td>{{ selectedQuery.user }}</td>
              </tr>
            </table>
          </div>
        </div>
      </div>
      <template #foot>
        <ABtn kind="ghost" @click="queryDetailOpen = false">닫기</ABtn>
      </template>
    </AModal>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AModal from "@/components/Pineapple/AModal.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";

export default {
  name: "SystemDbAnalyzer",
  components: { APageHead, ACard, ABtn, APill, AStat, ATable, AModal, AEmpty },
  data() {
    return {
      stats: null,
      activeTab: 'slow',
      tabs: [
        { id: 'slow',     label: '슬로우 쿼리',         icon: 'hourglass_top' },
        { id: 'frequent', label: '자주 실행되는 쿼리',  icon: 'trending_up' },
        { id: 'indexes',  label: '인덱스 현황',         icon: 'list_alt' },
      ],
      slowQueries: [],
      loadingSlowQueries: false,
      slowFilters: { threshold: 1, page: 1, page_size: 20 },
      slowTotal: 0,
      slowCurrentPage: 1,
      slowTotalPages: 1,
      slowCols: [
        { key: 'mean_time_sec', label: '평균 시간', width: 110 },
        { key: 'max_time_sec',  label: '최대 시간', width: 110 },
        { key: 'calls',         label: '호출 횟수', width: 100 },
        { key: 'query',         label: '쿼리' },
        { key: 'actions',       label: '작업', width: 70, align: 'center' },
      ],
      frequentQueries: [],
      loadingFrequentQueries: false,
      frequentChart: null,
      frequentCols: [
        { key: 'rank',          label: '순위', width: 60, align: 'center' },
        { key: 'query',         label: '쿼리' },
        { key: 'calls',         label: '실행 횟수', width: 110 },
        { key: 'mean_time_ms',  label: '평균 시간', width: 120 },
        { key: 'total_time_ms', label: '총 시간',   width: 120 },
        { key: 'hit_rate',      label: 'Hit Rate',  width: 100 },
        { key: 'actions',       label: '작업', width: 70, align: 'center' },
      ],
      indexes: [],
      unusedIndexes: [],
      indexSummary: null,
      loadingIndexes: false,
      indexFilters: { table_name: '' },
      indexCols: [
        { key: 'table_name',  label: '테이블명' },
        { key: 'index_name',  label: '인덱스명' },
        { key: 'index_size',  label: '인덱스 크기', width: 110 },
        { key: 'idx_scan',    label: '스캔 횟수',   width: 110 },
        { key: 'status',      label: '상태', width: 100, align: 'center' },
      ],
      selectedQuery: null,
      queryDetailOpen: false,
    };
  },
  computed: {
    slowDisplayPages() {
      const pages = [];
      const maxDisplay = 5;
      const half = Math.floor(maxDisplay / 2);
      let start = Math.max(1, this.slowCurrentPage - half);
      let end = Math.min(this.slowTotalPages, start + maxDisplay - 1);
      if (end - start < maxDisplay - 1) {
        start = Math.max(1, end - maxDisplay + 1);
      }
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
  },
  watch: {
    activeTab(newTab) {
      if (newTab === 'frequent' && this.frequentQueries.length > 0) {
        this.$nextTick(() => this.renderFrequentChart());
      }
    },
  },
  mounted() {
    this.fetchStats();
    this.fetchSlowQueries();
    this.fetchFrequentQueries();
    this.fetchIndexes();
  },
  beforeUnmount() {
    if (this.frequentChart) this.frequentChart.destroy();
  },
  methods: {
    async refreshAll() {
      await Promise.all([
        this.fetchStats(),
        this.fetchSlowQueries(),
        this.fetchFrequentQueries(),
        this.fetchIndexes(),
      ]);
      this.$notifyVue({ type: "success", title: "데이터가 새로고침되었습니다." });
    },

    async fetchStats() {
      try {
        const response = await this.$axios({ method: "get", url: "/system/db/stats" });
        this.stats = response.data;
      } catch (error) {
        console.error("통계 조회 실패:", error);
      }
    },

    async fetchSlowQueries() {
      this.loadingSlowQueries = true;
      try {
        const response = await this.$axios({
          method: "get",
          url: "/system/db/slow-queries",
          params: {
            threshold: this.slowFilters.threshold,
            page: this.slowCurrentPage,
            page_size: this.slowFilters.page_size,
          },
          loadingBar: true,
        });
        this.slowQueries = response.data.items || [];
        this.slowTotal = response.data.total || 0;
        this.slowTotalPages = Math.ceil(this.slowTotal / this.slowFilters.page_size);
      } catch (error) {
        console.error("슬로우 쿼리 조회 실패:", error);
        this.slowQueries = [];
      } finally {
        this.loadingSlowQueries = false;
      }
    },

    changeSlowPage(page) {
      if (page < 1 || page > this.slowTotalPages) return;
      this.slowCurrentPage = page;
      this.fetchSlowQueries();
    },

    async fetchFrequentQueries() {
      this.loadingFrequentQueries = true;
      try {
        const response = await this.$axios({
          method: "get",
          url: "/system/db/frequent-queries",
          params: { limit: 10 },
          loadingBar: true,
        });
        this.frequentQueries = response.data.items || [];
        this.$nextTick(() => {
          if (this.activeTab === 'frequent') this.renderFrequentChart();
        });
      } catch (error) {
        console.error("자주 실행되는 쿼리 조회 실패:", error);
        this.frequentQueries = [];
      } finally {
        this.loadingFrequentQueries = false;
      }
    },

    renderFrequentChart() {
      if (this.frequentChart) this.frequentChart.destroy();

      const canvas = this.$refs.frequentChart;
      if (!canvas || this.frequentQueries.length === 0) return;

      const ctx = canvas.getContext('2d');
      const labels = this.frequentQueries.map((_, i) => `Query ${i + 1}`);
      const data = this.frequentQueries.map(q => q.calls);

      this.frequentChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: '실행 횟수',
            data,
            backgroundColor: 'rgba(201, 138, 10, 0.7)',
            hoverBackgroundColor: 'rgba(201, 138, 10, 0.9)',
            borderRadius: 6,
            borderSkipped: false,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                title: (items) => {
                  const idx = items[0].dataIndex;
                  const q = this.frequentQueries[idx];
                  return q.query.substring(0, 50) + '...';
                },
                label: (item) => `실행 횟수: ${this.formatNumber(item.raw)}`,
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: { callback: (v) => this.formatNumber(v) },
            },
          },
        },
      });
    },

    async fetchIndexes() {
      this.loadingIndexes = true;
      try {
        const params = {};
        if (this.indexFilters.table_name) params.table_name = this.indexFilters.table_name;
        const response = await this.$axios({
          method: "get",
          url: "/system/db/indexes",
          params,
          loadingBar: true,
        });
        this.indexes = response.data.items || [];
        this.unusedIndexes = response.data.unused_indexes || [];
        this.indexSummary = response.data.summary || null;
      } catch (error) {
        console.error("인덱스 현황 조회 실패:", error);
        this.indexes = [];
      } finally {
        this.loadingIndexes = false;
      }
    },

    showQueryDetail(query) {
      this.selectedQuery = query;
      this.queryDetailOpen = true;
    },

    formatMs(v) {
      if (v === undefined || v === null) return '0';
      return parseFloat(v).toFixed(2);
    },
    formatSec(v) {
      if (v === undefined || v === null) return '0';
      return parseFloat(v).toFixed(3);
    },
    formatNumber(v) {
      if (v === undefined || v === null) return '0';
      return new Intl.NumberFormat('ko-KR').format(v);
    },
    formatPercent(v) {
      if (v === undefined || v === null) return '0';
      return parseFloat(v).toFixed(1);
    },
    formatTotalTime(ms) {
      if (!ms) return '0 ms';
      if (ms < 1000) return `${this.formatMs(ms)} ms`;
      if (ms < 60000) return `${(ms / 1000).toFixed(2)} s`;
      if (ms < 3600000) return `${(ms / 60000).toFixed(2)} min`;
      return `${(ms / 3600000).toFixed(2)} hr`;
    },
    hitRateKind(rate) {
      if (rate >= 95) return 'ok';
      if (rate >= 80) return 'warn';
      return 'err';
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.db-analyzer {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;

    @media (max-width: 992px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 576px) { grid-template-columns: 1fr; }
  }

  &__stat-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
  }

  &__stat-icon {
    width: 44px;
    height: 44px;
    border-radius: $p-radius-md;
    background: $p-point-soft;
    color: $p-point-deep;
    display: grid;
    place-items: center;
    flex-shrink: 0;

    > i { font-size: 22px; }

    &--warn { background: $p-warn-soft; color: $p-warn; }
    &--info { background: $p-info-soft; color: $p-info; }
    &--ok   { background: $p-ok-soft;   color: $p-ok; }
  }

  &__stat-foot {
    margin: 6px 0 0;
    font-size: 11px;
    color: $p-ink-soft;
  }

  &__pane {
    padding: 20px;
  }

  &__filters {
    align-items: end;
    margin-bottom: 18px;
  }

  &__filter-action {
    display: flex;
    align-items: end;

    .a-btn { height: 38px; }
  }

  &__num {
    font-family: $p-font-mono;
    font-variant-numeric: tabular-nums;
    font-size: 12px;
  }

  &__query {
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-ink-mid;
    max-width: 480px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__code {
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-point-deep;
    background: $p-point-soft;
    padding: 2px 6px;
    border-radius: 4px;
  }

  &__sub-h {
    margin: 0 0 12px;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    > i { font-size: 16px; color: $p-point-deep; }
  }

  &__chart-card {
    margin-bottom: 18px;
  }

  &__chart-wrap {
    position: relative;
    height: 300px;

    :deep(canvas) {
      max-height: 100% !important;
    }
  }

  &__alert {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 14px;
    background: $p-warn-soft;
    border-left: 3px solid $p-warn;
    border-radius: $p-radius-md;
    color: $p-warn;
    font-size: 12px;
    margin-bottom: 18px;

    > i { font-size: 18px; }
  }

  &__idx-summary {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 18px;

    @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
  }

  &__idx-stat {
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__idx-val {
    font-family: $p-font-mono;
    font-size: 22px;
    font-weight: 900;
    color: $p-point-deep;
    line-height: 1;

    &--ok   { color: $p-ok; }
    &--err  { color: $p-err; }
    &--info { color: $p-info; }
  }

  &__idx-label {
    font-size: 10px;
    font-weight: 700;
    color: $p-ink-soft;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  &__pager {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin-top: 16px;
  }

  &__pager-num {
    min-width: 32px;
    height: 32px;
    padding: 0 8px;
    border-radius: $p-radius;
    border: 1px solid $p-line;
    background: $p-panel;
    color: $p-ink-mid;
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    &:hover {
      background: $p-point-soft;
      color: $p-point-deep;
    }

    &.is-active {
      background: $p-point-deep;
      border-color: $p-point-deep;
      color: $p-panel;
    }
  }

  // ── Detail modal ──
  &__detail {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  &__detail-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__detail-h {
    margin: 0 0 6px;
    font-size: 12px;
    font-weight: 800;
    color: $p-ink2;
  }

  &__detail-code {
    margin: 0;
    background: $p-ink;
    color: $p-point;
    padding: 14px 16px;
    border-radius: $p-radius-md;
    font-family: $p-font-mono;
    font-size: 12px;
    line-height: 1.55;
    white-space: pre-wrap;
    word-break: break-all;
    max-height: 240px;
    overflow-y: auto;
  }

  &__detail-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @media (max-width: 768px) { grid-template-columns: 1fr; }
  }

  &__detail-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;

    th, td {
      padding: 8px 12px;
      border: 1px solid $p-line;
      text-align: left;
    }

    th {
      background: $p-panel-alt;
      color: $p-ink-mid;
      font-weight: 800;
      width: 40%;
    }
  }
}

.p-tabs {
  display: flex;
  border-bottom: 1px solid $p-line;
  padding: 0 12px;
  overflow-x: auto;

  &__item {
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    font-family: $p-font-body;
    font-size: 12px;
    font-weight: 700;
    padding: 12px 18px;
    border-bottom: 3px solid transparent;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: color $p-dur $p-ease, border-color $p-dur $p-ease;
    margin-bottom: -1px;

    > i { font-size: 16px; }

    &:hover { color: $p-ink; }

    &.is-active {
      color: $p-point-deep;
      border-bottom-color: $p-point-deep;
    }
  }
}
</style>
